import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownToLine, Trash2 } from 'lucide-react';
import type {
  SmartoltMigration,
  SmartoltMigrationStatus,
  SmartoltMigrationSummary,
} from '@simpleacs/shared';
import { SMARTOLT_MIGRATION_GROUP_LABELS } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatRelative, formatTimestamp } from '../../lib/format';
import { useSession } from '../../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
  type PillTone,
} from '../../components/ui';
import { MIGRATIONS_QUERY_KEY } from './MigrateDialog';

/**
 * Every migration taken, applied or not.
 *
 * A review nobody applied is a draft and stays here until it expires or is
 * discarded; a partly applied one is the resumable case — Open shows its
 * review again with Apply still offered for the groups that are pending or
 * failed. The list carries what the summary carries (status, when, by whom,
 * which groups); the per-group outcome lives on the review, which is one
 * click away rather than eight columns wide.
 */

const STATUS_TONE: Record<SmartoltMigrationStatus, PillTone> = {
  draft: 'primary',
  applying: 'info',
  applied: 'success',
  partial: 'warn',
  expired: 'neutral',
  discarded: 'neutral',
};

const STATUS_HINT: Record<SmartoltMigrationStatus, string> = {
  draft: 'Reviewed, not applied. Open it to apply, or discard it.',
  applying: 'Being applied now — the tray shows its progress.',
  applied: 'Every chosen group landed.',
  partial: 'Some groups landed, some did not. Open it to apply the rest.',
  expired: 'The fleet has moved since this was read; take a new one.',
  discarded: 'Dropped without being applied.',
};

export function MigrationsCard({ onOpen }: { onOpen: (m: SmartoltMigration) => void }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [...MIGRATIONS_QUERY_KEY],
    queryFn: () => api.get<{ items: SmartoltMigrationSummary[] }>('/smartolt/migrations'),
    // A migration being applied changes on its own; the list should notice.
    refetchInterval: (q) =>
      q.state.data?.items.some((m) => m.status === 'applying') ? 5_000 : false,
  });

  const open = useMutation({
    mutationFn: (id: string) =>
      api.get<SmartoltMigration>(`/smartolt/migrations/${encodeURIComponent(id)}`),
    onSuccess: onOpen,
  });

  const discard = useMutation({
    mutationFn: (id: string) => api.delete(`/smartolt/migrations/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MIGRATIONS_QUERY_KEY }),
  });

  const items = query.data?.items ?? [];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ArrowDownToLine className="size-4 text-muted" />
          Migrations from SmartOLT
        </span>
      }
    >
      {query.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : query.error ? (
        <div className="p-5">
          <ErrorNotice error={query.error} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No migrations yet"
          description="Migrate from SmartOLT reads their whole inventory, shows one frozen diff per group, and writes only what you confirm."
        />
      ) : (
        <>
          {(open.error || discard.error) && (
            <div className="px-5 pt-4">
              <ErrorNotice error={open.error ?? discard.error} />
            </div>
          )}
          <Table>
            <THead>
              <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Taken</th>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">From</th>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">Brings</th>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">Status</th>
              <th className="px-5 py-2.5 text-right text-xs font-medium text-muted"></th>
            </THead>
            <TBody>
              {items.map((m) => {
                const resumable = m.status === 'draft' || m.status === 'partial';
                return (
                  <TRow key={m.id}>
                    <td className="px-5 py-3 align-top">
                      <p className="text-xs text-ink">{formatTimestamp(m.createdAt)}</p>
                      <p className="text-[11px] text-faint">
                        {formatRelative(m.createdAt)}
                        {m.createdBy ? ` · ${m.createdBy}` : ''}
                        {m.appliedAt ? ` · applied ${formatRelative(m.appliedAt)}` : ''}
                      </p>
                    </td>
                    <td className="px-3 py-3 align-top font-mono text-xs text-muted">{m.baseUrl}</td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        {m.groups.map((g) => (
                          <Pill key={g} tone="neutral">
                            {SMARTOLT_MIGRATION_GROUP_LABELS[g]}
                          </Pill>
                        ))}
                      </div>
                      {m.warnings.length > 0 && (
                        <p className="mt-1 text-[11px] text-warn" title={m.warnings.join('\n')}>
                          {m.warnings.length} warning{m.warnings.length === 1 ? '' : 's'} at read time
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <Pill state tone={STATUS_TONE[m.status]} className="cursor-help" >
                        <span title={STATUS_HINT[m.status]}>{m.status}</span>
                      </Pill>
                    </td>
                    <td className="px-5 py-3 align-top">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={open.isPending && open.variables === m.id}
                          onClick={() => open.mutate(m.id)}
                          title={
                            resumable
                              ? 'Open the review; Apply is offered for the groups still pending or failed'
                              : 'Open the review, read-only'
                          }
                        >
                          Open
                        </Button>
                        {can('olt.manage') && resumable && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-danger"
                            icon={<Trash2 className="size-3.5" />}
                            loading={discard.isPending && discard.variables === m.id}
                            onClick={() => {
                              if (
                                confirm(
                                  m.status === 'partial'
                                    ? 'Discard this migration? What has already landed stays; the groups not yet applied are dropped and would need a new review.'
                                    : 'Discard this review? Nothing has been written; a new one can be taken at any time.',
                                )
                              ) {
                                discard.mutate(m.id);
                              }
                            }}
                          >
                            Discard
                          </Button>
                        )}
                      </div>
                    </td>
                  </TRow>
                );
              })}
            </TBody>
          </Table>
        </>
      )}
    </Card>
  );
}
